import styles from "./Brand.module.scss";

export interface VenueCntrlWordmarkProps {
  /** Rendered HEIGHT in px. The 463.22×50.71 viewBox drives the width. */
  size?: number;
  className?: string;
  /**
   * Draw it as DECORATIVE (`aria-hidden`, no role/label) instead of an `img` named "Venue CNTRL". For a
   * lockup that sits beside text already carrying the name. The default is labelled — the booking banner
   * relies on it to complete "…land soon with Venue CNTRL" as the region's accessible name.
   */
  decorative?: boolean;
}

/**
 * The VENUE CNTRL wordmark. Ported **byte-identical** from the Venue CNTRL brand kit
 * (`venue-cntrl-old/venuecntrl-40.svg` — the exact wordmark the venue app's own shell renders in its
 * topbar), the 10 glyph paths (VENUE + CNTRL). Rendered in `currentColor` (the kit fills `#0e0c0b`; the
 * consumer sets the colour — white on the booking page's dark hero). Mirrors the Artist CNTRL `Wordmark` and
 * `AntennaMark` ports; the Venue CNTRL MARK is the shared "C" glyph already shipped as `BrandMark`.
 *
 * `size` is the rendered height in px; the 463.22×50.71 viewBox drives the width.
 */
export function VenueCntrlWordmark({ size = 24, className, decorative = false }: VenueCntrlWordmarkProps) {
  const w = Math.round(size * (463.22 / 50.71) * 100) / 100;
  return (
    <svg
      viewBox="0 0 463.22 50.71"
      width={w}
      height={size}
      className={className ? `${styles.wordmark} ${className}` : styles.wordmark}
      fill="currentColor"
      {...(decorative ? { "aria-hidden": true } : { role: "img", "aria-label": "Venue CNTRL" })}
    >
      <path d="M11.12.98l11.9,48.76h-9.75L0,.98h11.12ZM32.77,49.74h-9.75L34.91.98h11.12l-13.26,48.76Z" />
      <path d="M47.98.98h37.06v9.43h-26.33v10.21h23.08v9.43h-23.08v10.27h26.98v9.43h-37.71V.98Z" />
      <path d="M91.21.98h10.08l.65,48.76h-10.73V.98ZM101.29.98h9.3l13.78,48.76h-9.3L101.29.98ZM134.45,49.74h-10.08l-.65-48.76h10.73v48.76Z" />
      <path d="M180.28,33.61c0,5.9-1.58,10.22-4.75,12.97-3.17,2.75-8.24,4.13-15.21,4.13s-12.06-1.38-15.25-4.13c-3.19-2.75-4.78-7.08-4.78-12.97V.98h10.73v32.05c0,2.9.8,4.95,2.41,6.14s3.9,1.79,6.89,1.79,5.44-.59,6.96-1.79c1.52-1.19,2.28-3.24,2.28-6.14V.98h10.73v32.64Z" />
      <path d="M186.13.98h37.06v9.43h-26.33v10.21h23.08v9.43h-23.08v10.27h26.98v9.43h-37.71V.98Z" />
      <path d="M285.67,45.25c-1.34.91-2.7,1.7-4.06,2.37-1.37.67-2.81,1.25-4.32,1.72-1.52.48-3.15.82-4.91,1.04s-3.7.33-5.82.33c-3.68,0-6.89-.44-9.62-1.33-2.73-.89-4.97-2.33-6.73-4.32-1.76-1.99-3.07-4.62-3.93-7.87-.87-3.25-1.3-7.22-1.3-11.9s.43-8.7,1.3-11.93c.87-3.23,2.18-5.83,3.93-7.8,1.75-1.97,4-3.39,6.73-4.26,2.73-.87,5.94-1.3,9.62-1.3,2.04,0,3.9.1,5.59.29,1.69.19,3.28.51,4.78.94,1.5.43,2.94.97,4.32,1.62s2.77,1.43,4.16,2.34l-6.57,7.93c-1.39-1.13-2.98-1.97-4.78-2.54-1.8-.56-3.89-.84-6.27-.84-1.99,0-3.72.24-5.17.71-1.45.48-2.64,1.31-3.58,2.5-.93,1.19-1.62,2.79-2.05,4.78s-.65,4.51-.65,7.54.24,5.75.72,7.77c.48,2.01,1.18,3.61,2.11,4.78.93,1.17,2.11,1.97,3.54,2.41,1.43.43,3.08.65,4.94.65,4.77,0,8.58-1.19,11.44-3.58l6.57,7.93Z" />
      <path d="M288.85.98h10.08l.65,48.76h-10.73V.98ZM298.93.98h9.3l13.78,48.76h-9.3L298.93.98ZM332.09,49.74h-10.08l-.65-48.76h10.73v48.76Z" />
      <path d="M351.27,10.4h-14.63V.98h39.98v9.43h-14.63v39.33h-10.73V10.4Z" />
      <path d="M381.17.98h25.03c2.82,0,5.19.26,7.12.78,1.93.52,3.5,1.37,4.71,2.54,1.21,1.17,2.09,2.69,2.63,4.55.54,1.86.81,4.14.81,6.83,0,2.51-.24,4.67-.72,6.47-.48,1.8-1.24,3.28-2.28,4.45-1.04,1.17-2.41,2.06-4.1,2.67-1.69.61-3.75.98-6.18,1.11l15.67,19.37h-14.3l-13.98-19.31h-3.71v19.31h-10.73V.98ZM405.88,21c1.39,0,2.49-.42,3.32-1.27s1.24-2.2,1.24-4.06-.41-3.21-1.24-4.03-1.93-1.24-3.32-1.24h-13.98v10.6h13.98Z" />
      <path d="M427.13.98h10.73v39.33h25.35v9.43h-36.08V.98Z" />
    </svg>
  );
}
